import React, { useEffect, useState } from "react"
import {
	Box, Button, Chip, FormHelperText, IconButton, Link, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"
import { secondaryButtonSx } from "@components"
import { dsColorNew } from "@palette"
import { Close as CloseIcon, Add as AddIcon } from "Pages/DsRepricing/@components/icons"
import { toneChipSx } from "Pages/DsRepricing/@components/tones"
import { rangeChipSx } from "Pages/DsRepricing/Page/@components/rangeSx"
import { DS_REPRICING_ROUTES } from "@constants/routes"
import { dsRepricingDrawerData, dsRepricingDrawerErrors, dsRepricingListData } from "Pages/DsRepricing/Page/@signals"
import { dsRepricingToggleSx } from "Pages/DsRepricing/Page/@components/toggleSx"
import { alignConfirmOptions } from "Pages/DsRepricing/Page/@components/AlignConfirm"
import useConfirm from "Pages/DsRepricing/@components/useConfirm"
import {
	alignHours, cadenceErrors, factoryCadence, fmtInt, pickupDatesLine, WEEKDAY_CODES, WEEKDAY_SHORT, zeroSearchesReason,
} from "Pages/DsRepricing/Page/@services/cadence"
import { readPeriod } from "Pages/DsRepricing/Page/@components/periodShape"
import { useDraftRateShops, useTenantRateShops } from "Pages/DsRepricing/Page/@hooks/useRateShops"

// Card 3 of Basic, "When does it run?" (CU-869fhuhy6, UX D4/D5, brief v3).
// The scheduler fields of the old schedule rule: frequency, window, days it
// runs. No pickup horizon since CU-869fkr1a9 (criteria 16-18): the pickup
// dates are those of "When does it apply", and the pickup / dropoff time of
// the rentals searched is set in that card too (Curro, 2026-10-21) — it
// still travels in `whenItRuns` (`PickupDates.jsx`). The cadence is ALWAYS this
// strategy's own (no tenant default since brief v3): a new strategy
// starts from the factory proposal, MARKED as such (UX v3 D19: chip
// "factory proposal — review it", gone the moment any field is edited
// edits it in place. It is
// `whenItRuns` of the drawer payload and travels with the save.
//
// The number under the card is the engine's (`POST /strategies/rate-shops`
// with the draft, UX D5): nothing is multiplied here.
//
// `CadenceFields` is controlled and has no idea where its value comes from.

const HALF_HOUR = { step: 1800 }
// Never the bare word "weekdays" (criterion 24): "Mon–Fri".
const WEEKDAY_PRESETS = [
	{ label: 'Every day', days: [...WEEKDAY_CODES] },
	{ label: 'Mon–Fri', days: ['mon', 'tue', 'wed', 'thu', 'fri'] },
	{ label: 'Weekend', days: ['sat', 'sun'] },
]

const strategiesHref = DS_REPRICING_ROUTES.STRATEGIES_PATH

// A link in the app's blue
const linkSx = { color: dsColorNew.appBlue, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }

export const mutedSx = (muted) => (muted ? { '& .MuiInputBase-root': { bgcolor: dsColorNew.pillTrackBg } } : { '& .MuiInputBase-root': { bgcolor: dsColorNew.white } })

// The field label of the wizard's cards (Weekdays, Period): 14/600 dark, the hint 12 muted under it
export function Label({ children, hint }) {
	return (
		<Box sx={{ mb: 1 }}>
			<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textDark }}>{children}</Typography>
			{hint && <Typography component="span" sx={{ display: 'block', fontSize: 12, color: dsColorNew.textMuted }}>{hint}</Typography>}
		</Box>
	)
}

// The quiet preset chips of the wizard's list fields (seasons, durations): offer, do not toggle
const presetChipSx = {
	height: 26, fontSize: 12, fontWeight: 500, bgcolor: dsColorNew.white, color: dsColorNew.textDark,
	borderColor: dsColorNew.grayBorder, '&:hover': { borderColor: dsColorNew.appBlue, bgcolor: dsColorNew.hoverBgLight },
}

// The line under the card with the engine's count: grey while there is nothing to count, soft blue
// with the number, amber when the tenant would go over its warning
const lineSx = (tone) => ({
	mt: 2.5, p: 1.5, borderRadius: '10px', border: '1px solid',
	...(tone === 'warn' ? { bgcolor: dsColorNew.noteAmberBg, borderColor: dsColorNew.noteAmberBorder }
	   : tone === 'ok' ? { bgcolor: dsColorNew.pillActiveBg, borderColor: dsColorNew.noteInfoBorder }
	   : { bgcolor: dsColorNew.tableRowAlt, borderColor: dsColorNew.grayBorder }),
})
const lineTextSx = { fontSize: 13, color: dsColorNew.textMuted, lineHeight: 1.5 }

function SpecificHours({ value, onChange, muted }) {
	const [newHour, setNewHour] = useState('09:00')
	const add = () => {
		if (!/^\d\d:\d\d$/.test(newHour) || value.includes(newHour)) return
		onChange([...value, newHour].sort())
	}
	return (
		<Box>
			<Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 1, minHeight: 28 }}>
				{value.length === 0
					? <Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, fontStyle: 'italic' }}>No hours yet. Add at least one.</Typography>
					: value.map(h => (
						<Chip key={h} label={h} size="small" onDelete={() => onChange(value.filter(x => x !== h))}
						      sx={{ ...rangeChipSx, fontWeight: 600, fontSize: 13 }}/>
					))}
			</Box>
			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
				<TextField size="small" type="time" inputProps={HALF_HOUR} value={newHour}
				           onChange={e => setNewHour(e.target.value)} sx={{ width: 140, ...mutedSx(muted) }}/>
				<Button size="small" variant="outlined" onClick={add} startIcon={<AddIcon sx={{ fontSize: 14 }}/>} sx={{ ...secondaryButtonSx, height: 36 }}>
					Add hour
				</Button>
			</Box>
		</Box>
	)
}

export function HourField({ label, value, onChange, muted }) {
	return (
		<Box sx={{ flex: 1, minWidth: 180 }}>
			<Label>{label}</Label>
			<Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
				<TextField size="small" type="time" inputProps={HALF_HOUR} value={value || ''}
				           onChange={e => onChange(e.target.value || null)} sx={{ flex: 1, ...mutedSx(muted) }}/>
				{value && (
					<IconButton size="small" onClick={() => onChange(null)} aria-label={`Clear ${label}`}>
						<CloseIcon fontSize="small"/>
					</IconButton>
				)}
			</Box>
			<Typography component="span" sx={{ display: 'block', fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
				{value ? `Only rate shops with ${label.toLowerCase()} at ${value}` : 'Any time'}
			</Typography>
		</Box>
	)
}

/** The cadence fields, controlled. `errors` is `cadenceErrors(value)`; `muted` paints the inputs grey. */
export function CadenceFields({ value, onChange, errors = {}, muted = false }) {
	const c = value || {}
	const set = (patch) => onChange({ ...c, ...patch })
	const specific = c.frequencyKind === 'specific_hours'
	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
			<Box>
				<Label>Frequency</Label>
				<ToggleButtonGroup exclusive size="small" value={c.frequencyKind || 'every_n_hours'} sx={{ ...dsRepricingToggleSx, mb: 1.5 }}
				                   onChange={(_, v) => v && set({ frequencyKind: v, frequencyValue: v === 'specific_hours' ? [] : 2 })}>
					<ToggleButton value="every_n_hours" title="Regular cadence inside a window">Every N hours</ToggleButton>
					<ToggleButton value="specific_hours" title="A literal list">Specific hours</ToggleButton>
				</ToggleButtonGroup>
				{specific
					? <SpecificHours value={Array.isArray(c.frequencyValue) ? c.frequencyValue : []} onChange={v => set({ frequencyValue: v })} muted={muted}/>
					: (
						<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
							<TextField size="small" type="number" label="Every" value={c.frequencyValue ?? ''}
							           onChange={e => set({ frequencyValue: e.target.value === '' ? '' : Number(e.target.value) })}
							           inputProps={{ min: 1, max: 24 }}
							           InputProps={{ endAdornment: <Typography sx={{ color: dsColorNew.textMuted, fontSize: 13, ml: 0.5 }}>hours</Typography> }}
							           error={!!errors.frequency} helperText={errors.frequency}
							           sx={{ width: 170, ...mutedSx(muted) }}/>
							<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
								<TextField size="small" type="time" inputProps={HALF_HOUR} label="Window from" value={c.windowStart || ''}
								           onChange={e => set({ windowStart: e.target.value })} InputLabelProps={{ shrink: true }}
								           error={!!errors.window} sx={{ width: 150, ...mutedSx(muted) }}/>
								<Box sx={{ color: dsColorNew.textSubtle }}>—</Box>
								<TextField size="small" type="time" inputProps={HALF_HOUR} label="to" value={c.windowEnd || ''}
								           onChange={e => set({ windowEnd: e.target.value })} InputLabelProps={{ shrink: true }}
								           error={!!errors.window} sx={{ width: 130, ...mutedSx(muted) }}/>
							</Box>
							{errors.window && <FormHelperText error sx={{ width: '100%', mt: -1 }}>{errors.window}</FormHelperText>}
						</Box>
					)}
				{specific && errors.frequency && <FormHelperText error>{errors.frequency}</FormHelperText>}
			</Box>

			<Box>
				<Label hint="The pickup weekdays of the rentals priced are in Dates & LOR, card 1 — this is when the engine fires.">Days it runs (the engine fires on…)</Label>
				<ToggleButtonGroup size="small" value={c.weekdays || []} onChange={(_, v) => set({ weekdays: v || [] })}
				                   sx={{ ...dsRepricingToggleSx, width: '100%', '& .MuiToggleButton-root': { ...dsRepricingToggleSx['& .MuiToggleButton-root'], flex: 1 } }}>
					{WEEKDAY_CODES.map(d => <ToggleButton key={d} value={d}>{WEEKDAY_SHORT[d]}</ToggleButton>)}
				</ToggleButtonGroup>
				<Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
					{WEEKDAY_PRESETS.map(p => (
						<Chip key={p.label} label={p.label} size="small" variant="outlined" clickable onClick={() => set({ weekdays: p.days })} sx={presetChipSx}/>
					))}
				</Box>
				{errors.weekdays && <FormHelperText error>{errors.weekdays}</FormHelperText>}
			</Box>

		</Box>
	)
}

const breakdownText = (e) => {
	const b = e?.breakdown || {}
	const parts = []
	if (b.offices != null) parts.push(`${fmtInt(b.offices)} office${b.offices === 1 ? '' : 's'}`)
	if (b.pickup_dates != null) parts.push(`${fmtInt(b.pickup_dates)} pickup date${b.pickup_dates === 1 ? '' : 's'}`)
	const head = `${fmtInt(e?.searches_per_run)} per run${parts.length ? ` (${parts.join(' × ')})` : ''}`
	const inside = []
	if (b.sites != null) inside.push(`${b.sites} site${b.sites === 1 ? '' : 's'}`)
	if (b.lors != null) inside.push(`${b.lors} LOR${b.lors === 1 ? '' : 's'}`)
	const tasks = e?.tasks_per_run != null ? ` — ${inside.join(' and ') || 'sites and LORs'} travel inside each search: ${fmtInt(e.tasks_per_run)} task${e.tasks_per_run === 1 ? '' : 's'} per run` : ''
	return `${head}${tasks}`
}

/**
 * The rate-shop line at the foot of the card: the engine's count for the
 * draft as it stands (UX D5; criterion 27 "recomputed on every change,
 * without saving"). States: counting, cannot count (invalid cadence, no
 * product, no pickup dates — CU-869fkr1a9), zero with the engine's reason,
 * and the number with its breakdown as the engine sends it.
 */
export function SearchesLine({ estimate: e, loading, error, tenantTotal, warning, cadenceValid, hasProduct, hasPickupDates = true, advice = null }) {
	if (!hasProduct || !cadenceValid || !hasPickupDates) {
		return (
			<Box sx={lineSx('neutral')}>
				<Typography sx={lineTextSx}>
					<strong>—</strong> searches / day — {!hasProduct ? 'pick a product in card 2 first; the engine counts on the product\u2019s sites and brackets.'
						: !hasPickupDates ? 'no pickup dates yet: set lead times or seasons in Dates & LOR, card 1.'
						: 'fix the cadence above first; the count needs a valid window and days.'}
				</Typography>
			</Box>
		)
	}
	if (error) {
		return (
			<Box sx={lineSx('neutral')}>
				<Typography sx={lineTextSx}><strong>—</strong> searches / day — {error}</Typography>
			</Box>
		)
	}
	if (loading || !e) {
		return (
			<Box sx={lineSx('neutral')}>
				<Typography sx={lineTextSx}>Counting the searches with the engine…</Typography>
			</Box>
		)
	}
	if (e.searches_per_day == null) {
		const why = Object.entries(e.skipped || {}).filter(([, n]) => n).map(([k]) => k.replace(/_/g, ' ')).join(', ')
		return (
			<Box sx={lineSx('neutral')}>
				<Typography sx={lineTextSx}>
					<strong>—</strong> searches / day — the engine cannot compose this strategy yet{why ? ` (${why})` : ''}.
				</Typography>
			</Box>
		)
	}
	const over = warning > 0 && tenantTotal != null && tenantTotal > warning
	const shared = e.shared_per_day || 0
	return (
		<Box sx={lineSx(e.searches_per_day === 0 ? 'neutral' : over ? 'warn' : 'ok')}>
			<Typography sx={{ fontSize: 13, color: dsColorNew.textDark, lineHeight: 1.5 }}>
				{e.searches_per_day === 0 ? (
					<><strong>0</strong> searches / day — {zeroSearchesReason(e)}{e.runs_per_day === 0 ? ', and no run hour is set' : ''}.</>
				) : (
					<>
						<strong>{fmtInt(e.searches_per_day)}</strong>
						{shared > 0 && <Box component="span" sx={{ color: dsColorNew.successText }} title="Searches another strategy that is on also requests at the same hour: the engine launches them once for both."> ({fmtInt(shared)} shared)</Box>}
						{' '}searches / day — {breakdownText(e)} × {e.runs_per_day} run{e.runs_per_day === 1 ? '' : 's'}.
					</>
				)}
				{over && (
					<> With this strategy on, the tenant would launch <strong>{fmtInt(tenantTotal)} / day</strong> (shared searches counted once) — above the {fmtInt(warning)} warning set in{' '}
						<Box component="a" href={strategiesHref} target="_blank" rel="noopener noreferrer" sx={linkSx}>the Rate shops panel of Strategies</Box>.</>
				)}
			</Typography>
			{advice && (
				<Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${dsColorNew.grayBorder}`, display: 'flex', alignItems: 'center', gap: 1.25 }}>
					<IconSeeker icon="triangle-exclamation" sx={{ color: dsColorNew.noteAmber, fontSize: 14, flex: 'none' }}/>
					<Typography sx={{ flex: 1, fontSize: 13, color: dsColorNew.noteAmber, lineHeight: 1.45 }}>
						This strategy and <strong>{advice.other.name}</strong> run at <strong>{advice.mine.hours}</strong> and <strong>{advice.other.hours}</strong> on the same offices and dates → align them to share rate shops: <strong>saves ≈{fmtInt(advice.saves)}/day</strong>.
					</Typography>
					<Button size="small" variant="outlined" onClick={advice.onAlign}
					        title={`Run this strategy at ${advice.other.hours}, like "${advice.other.name}": frequency, window, days and hours. Asks first.`}
					        sx={{ ...secondaryButtonSx, whiteSpace: 'nowrap', flex: 'none' }}>
						Align to {advice.other.name}
					</Button>
				</Box>
			)}
			<Typography component="span" sx={{ display: 'block', mt: 0.75, fontSize: 12, color: dsColorNew.textMuted }}>
				Counted by the engine for this strategy as the form has it, with shared searches discounted · pickups searched = When does it apply · sites and LORs travel inside one search.
			</Typography>
		</Box>
	)
}

export default function DsRepricingWhenItRuns() {
	const data = dsRepricingDrawerData.value || {}
	const cadence = data.whenItRuns || factoryCadence()
	const hasPickupDates = pickupDatesLine(readPeriod(data), null) != null
	const errors = cadenceErrors(cadence)
	const isValid = Object.keys(errors).length === 0
	const [confirm, confirmDialog] = useConfirm()

	useEffect(() => {
		dsRepricingDrawerErrors.value = { ...dsRepricingDrawerErrors.value, cadence: isValid }
	}, [isValid, data.id])

	// A missing cadence on a loaded strategy (migrated without one) is
	// written into the draft as the factory proposal, so the save carries it.
	useEffect(() => {
		if (!data.whenItRuns && dsRepricingDrawerData.value && Object.keys(dsRepricingDrawerData.value).length) {
			dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, whenItRuns: factoryCadence() }
		}
	}, [data.whenItRuns])

	const tenant = useTenantRateShops()
	const warning = tenant.data?.warning ?? 0
	// One count for the drawer: the Pickup dates line of When does it apply reads the same answer.
	const shop = useDraftRateShops()
	// The tenant with this strategy on: the engine's answer for the draft carries the grouped total.
	const tenantTotal = shop.data?.grouped_total ?? tenant.data?.grouped_total ?? null
	const key = String(data.id ?? 'draft')
	const hint = (shop.data?.alignment_hints || []).find(h => String(h.a) === key || String(h.b) === key)
	const strategies = dsRepricingListData.value
	const advice = hint ? (() => {
		const iAmA = String(hint.a) === key
		const otherId = iAmA ? hint.b : hint.a
		const other = strategies.find(s => String(s.id) === String(otherId))
		if (!other) return null
		return {
			mine: { hours: ((iAmA ? hint.a_hours : hint.b_hours) || []).join(', ') || '—' },
			other: { id: other.id, name: other.name, hours: ((iAmA ? hint.b_hours : hint.a_hours) || []).join(', ') || '—' },
			saves: hint.saves_per_day,
			// Criterion 29 (UX v3 D21): confirm with Now · After; frequency, window, days and hours are copied.
			onAlign: async () => {
				const after = alignHours(cadence, other.whenItRuns || factoryCadence())
				if (!await confirm(alignConfirmOptions({ aName: other.name, bName: data.name || 'this strategy', before: cadence, after, saves: hint.saves_per_day }))) return
				dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, whenItRuns: after }
			},
		}
	})() : null

	// Editing any field makes the cadence the strategy's own (D19).
	const change = (next) => { dsRepricingDrawerData.value = { ...dsRepricingDrawerData.value, whenItRuns: { ...next, reviewed: true } } }

	return (
		<Box>
			<CadenceFields value={cadence} onChange={change} errors={errors}/>
			<SearchesLine estimate={shop.estimate} loading={shop.loading} error={shop.error}
			              tenantTotal={tenantTotal} warning={warning}
			              cadenceValid={isValid} hasProduct={!!data.productId} hasPickupDates={hasPickupDates} advice={advice}/>
			{confirmDialog}
		</Box>
	)
}
